import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PowerHeatmap } from "@/views/PowerHeatmap";
import ConsumptionsStat from "@/views/ConsumptionsStat";
import { HeadBanner } from "@/components/HeadBanner";
import { useStats } from "@/hooks/useStats";
import { useAuth } from "@/app/AuthContext";
import { UnitsExplorer, sanitizeTypePricing, type UnitsExplorerFilters } from "@/components/UnitsExplorer";
import { ClaimChargerModal } from "@/components/ClaimChargerModal";

const LS_VIEW = "dashboardViewMode_v3";
const LS_SORT = "dashboardSort_v2";
const LS_STATE = "dashboardState_v3";
const LS_TYPE = "dashboardType_v1";
const LS_PRICING = "dashboardPricing_v1";

function loadStringArray(key: string): string[] {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch { /* not JSON — ignore legacy value */ }
    return [];
}

export function DashboardPage() {
    const [filters, setFilters] = useState<UnitsExplorerFilters>(() => {
        // Drop a stale persisted pricing value if Commercial isn't selected, so
        // a leftover localStorage pricing can't silently filter on first load.
        const sane = sanitizeTypePricing(loadStringArray(LS_TYPE), loadStringArray(LS_PRICING));
        return {
            search: "",
            stateFilter: loadStringArray(LS_STATE),
            sortBy: localStorage.getItem(LS_SORT) || "name",
            viewMode: localStorage.getItem(LS_VIEW) === "vitrine" ? "vitrine" : "table",
            typeFilter: sane.typeFilter,
            pricingFilter: sane.pricingFilter,
            protocolFilter: [],
        };
    });

    // Persist filter changes to localStorage
    useEffect(() => localStorage.setItem(LS_STATE, JSON.stringify(filters.stateFilter)), [filters.stateFilter]);
    useEffect(() => localStorage.setItem(LS_SORT, filters.sortBy), [filters.sortBy]);
    useEffect(() => localStorage.setItem(LS_VIEW, filters.viewMode), [filters.viewMode]);
    useEffect(() => localStorage.setItem(LS_TYPE, JSON.stringify(filters.typeFilter)), [filters.typeFilter]);
    useEffect(() => localStorage.setItem(LS_PRICING, JSON.stringify(filters.pricingFilter)), [filters.pricingFilter]);

    const {
        loading,
        error,
        refetch: refetchStats,
        ...stats
    } = useStats();

    // Residential drivers can self-claim a charger; the empty state then offers
    // the claim CTA instead of the "contact your administrator" dead-end.
    const { isAdmin, isOrgUser } = useAuth();
    const canClaim = !isAdmin && !isOrgUser;
    const [claimOpen, setClaimOpen] = useState(false);

    // PowerHeatmap self-scopes via DashboardOrgContext: admin → fleet
    // (/api/power-heatmap), manager → selected org (/api/org/<id>/power-heatmap),
    // driver-only → renders nothing (scopeReady never becomes true).
    return (
        <div className="space-y-5">
            <HeadBanner refetch={refetchStats} stats={stats} error={error} loading={loading} />
            <PowerHeatmap />
            <ConsumptionsStat />

            <UnitsExplorer
                filters={filters}
                onFiltersChange={setFilters}
                canClaim={canClaim}
                onClaim={() => setClaimOpen(true)}
            />

            {canClaim && <ClaimChargerModal open={claimOpen} onOpenChange={setClaimOpen} />}
        </div>
    );
}
